/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import { Box, Icons } from 'components'
import { openUTMModal } from 'components/Modal/ModalManager'
import { truncateText } from 'pages/ai-discover/commonFunction'
import { useEffect, useState, useRef } from 'react'
import { useCreatePostStore } from 'stores/CreatePostStore'
import { IUrl } from 'types/PostInterface'
import { AppStore, T } from 'utils'
import { getActiveText, updateActive, updatePost } from 'utils/PostUtils/Post'
import {
  converUtmConfig,
  findWorkingUTM,
  getLinkProperties,
  newUtmUrl,
  toggleLinkShortening,
} from 'utils/PostUtils/ShortUrl'

/* ------------------- INTERFACES  */
interface Props {
  post: any
  firstComment?: boolean
  isDescription?: boolean
}

export const Utm = ({ post, firstComment, isDescription }: Props) => {
  /* ----------------- VARIABLES  */
  const firstCommentParameter = firstComment ? post : undefined

  // Riferimento all'URL originale per non perderlo quando si modifica il testo
  const originalUrlRef = useRef<string | null>(null)

  /* ----------------- STATE  */
  const [selectedIndex, setSelectedIndex] = useState(getLinkProperties(firstCommentParameter)!.length - 1)
  // @ts-ignore
  const [visibleLink, setVisibleLink]: IUrl = useState(findWorkingUTM(post, selectedIndex, firstComment))
  const [dropdownVisible, setDropdownVisible] = useState(false)

  /* ----------------- USEEFFECT  */
  // Salva l'URL originale quando viene inizializzato
  useEffect(() => {
    if (visibleLink?.originalUrl) {
      originalUrlRef.current = visibleLink.originalUrl
    }
  }, [])

  // Cambia visible link in base al l'index selezionato
  useEffect(() => {
    const newLink = findWorkingUTM(post, selectedIndex, firstComment)
    setVisibleLink(newLink)
    if (newLink?.originalUrl) {
      originalUrlRef.current = newLink.originalUrl
    }
  }, [selectedIndex, post])

  // Setta l'ultimo link come default
  useEffect(() => {
    setSelectedIndex(getLinkProperties(firstCommentParameter)!.length - 1)
  }, [getLinkProperties(firstCommentParameter)!.length])

  // Quando inserisco utmConfigs vengano aggiornati nel text
  useEffect(() => {
    if (visibleLink && !visibleLink?.shortened) {
      if (firstComment === undefined) {
        // Logica per il non-firstComment rimane invariata
        const actualLinkValue = visibleLink.shortened ? visibleLink?.shortUrl : visibleLink.originalUrl
        const newText = getActiveText().replace(actualLinkValue, newUtmUrl(visibleLink, converUtmConfig(visibleLink)))
        updateActive({ text: newText })
      }

      if (firstComment !== undefined) {
        // Usa il riferimento all'URL originale se disponibile
        const actualLinkValue = originalUrlRef.current || visibleLink.originalUrl

        // Ottieni il testo corrente
        const originalText = post?.data?.firstComment.text
        const newUtmString = newUtmUrl(visibleLink, converUtmConfig(visibleLink))

        try {
          // Trova tutte le occorrenze dell'URL nel testo
          let newText = originalText
          const urlIndex = originalText.indexOf(actualLinkValue)

          if (urlIndex >= 0) {
            // Sostituzione precisa basata sulla posizione
            newText =
              originalText.substring(0, urlIndex) +
              newUtmString +
              originalText.substring(urlIndex + actualLinkValue.length)

            // Aggiorna solo se c'è stato un cambiamento effettivo
            updatePost(post.variantId, {
              firstComment: {
                ...post?.data?.firstComment,
                text: newText,
              },
            })
          }
        } catch (error) {
          console.error("Errore durante l'aggiornamento UTM:", error)
        }
      }
    }
  }, [visibleLink?.utmConfig])

  // Gestisce il toggle tra shortened e non-shortened
  useEffect(() => {
    if (visibleLink && firstComment !== undefined && post?.data?.firstComment) {
      const originalText = post?.data?.firstComment.text

      // Determina quale URL cercare e con quale sostituire
      const fromUrl = visibleLink.shortened ? originalUrlRef.current : visibleLink.shortUrl
      const toUrl = visibleLink.shortened ? visibleLink.shortUrl : originalUrlRef.current

      if (fromUrl && toUrl && originalText.includes(fromUrl)) {
        try {
          // Sostituisci l'URL nel testo
          const urlIndex = originalText.indexOf(fromUrl)
          if (urlIndex >= 0) {
            const newText =
              originalText.substring(0, urlIndex) + toUrl + originalText.substring(urlIndex + fromUrl.length)

            // Aggiorna solo se necessario
            updatePost(post.variantId, {
              firstComment: {
                ...post?.data?.firstComment,
                text: newText,
              },
            })
          }
        } catch (error) {
          console.error('Errore durante lo switch shortened/expanded:', error)
        }
      }
    }
  }, [visibleLink?.shortened])

  const removeDuplicatedUrls = (urls: IUrl[]) => {
    // check urls of the active post and remove the duplicated ones
    const uniqueUrls = urls.filter((url, index) => {
      return urls.findIndex((u) => u.originalUrl === url.originalUrl) === index
    })
    return uniqueUrls
  }

  // remove duplicated links
  const urlsToChecks = removeDuplicatedUrls(getLinkProperties(firstCommentParameter) ?? [])

  return (
    <UtmContainer>
      {/* Scelta del link */}
      <ButtonContainer
        key={`button-${visibleLink?.originalUrl}`}
        onClick={() => {
          setDropdownVisible((prev) => !prev)
        }}
        style={
          dropdownVisible
            ? { background: AppStore.theme.o.lightBlue, color: AppStore.theme.o.blue }
            : { background: AppStore.theme.o.lightestGrey, color: AppStore.theme.o.black }
        }
      >
        {truncateText(visibleLink?.originalUrl ?? visibleLink?.shortUrl, 20)}

        <DropDownContainer
          style={{
            display: dropdownVisible ? 'flex' : 'none',
          }}
        >
          {urlsToChecks.map((url, index) => (
            <LinkContainer
              key={encodeURIComponent(url.originalUrl)}
              style={{ color: selectedIndex === index ? AppStore.theme.o.blue : AppStore.theme.o.black }}
              onClick={() => {
                setSelectedIndex(index)
                setDropdownVisible(true)
              }}
            >
              {url.originalUrl}
            </LinkContainer>
          ))}
        </DropDownContainer>
      </ButtonContainer>

      {/* Accorcia / Espande link */}
      {
        <>
          {visibleLink?.shortened && (
            <ButtonContainer
              onClick={() => toggleLinkShortening(post, false, visibleLink, firstComment, isDescription)}
            >
              {T.postPage.showFullLink} <Icons.expand fill={AppStore.theme.o.black} />
            </ButtonContainer>
          )}
          {!visibleLink?.shortened && (
            <ButtonContainer onClick={() => toggleLinkShortening(post, true, visibleLink, firstComment, isDescription)}>
              {T.postPage.shortFullLink} <Icons.collapse fill={AppStore.theme.o.black} />
            </ButtonContainer>
          )}
        </>
      }

      {/* Apre modale UTM */}
      <ButtonContainer onClick={() => openUTMModal(post, visibleLink, firstComment, isDescription)}>
        UTM <Icons.utm fill={AppStore.theme.o.black} />
      </ButtonContainer>
    </UtmContainer>
  )
}

const UtmContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  height: 56px;
  width: 100%;
  border-radius: 14px;
  border: 1px solid ${() => AppStore.theme.o.lightGrey};
  background: ${() => AppStore.theme.o.surface};
`

const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 4px 14px;
  gap: 6px;
  border-radius: 12px;
  height: 40px;
  color: ${() => AppStore.theme.o.black};
  text-overflow: ellipsis;
  font-family: Aktiv Grotesk;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  &:hover {
    background: ${() => AppStore.theme.o.lightestGrey};
  }
`

const DropDownContainer = styled(Box)`
  position: absolute;
  bottom: 62px;
  left: 50%;
  transform: translateX(-50%);
  width: 98%;
  border-radius: 14px;
  background: ${() => AppStore.theme.o.lightGrey};
  border: 1px solid ${() => AppStore.theme.o.lightestGrey};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px;
  gap: 4px;
`

const LinkContainer = styled(Box)`
  font-family: Aktiv Grotesk;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 12px;
  word-wrap: break-word;

  color: ${() => AppStore.theme.o.darkGrey};
  background-color: ${() => AppStore.theme.o.lightGrey};

  &:hover {
    background-color: ${() => AppStore.theme.o.surface};
  }
`
